import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock3, MapPin, Truck, Users, Pencil, Briefcase, Plus, Loader2, PlugZap, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { InstructionBanner, PageTitle, Private, SearchBar, searchMatch } from "@/components/Bits";
import CustomerPageDialog, { PortalChip } from "@/components/portal/CustomerPageDialog";
import { fmtMoney } from "@/lib/format";
import { mapsUrl, fmtTime12 } from "@/lib/maps";
import {
  listJobsApi, patchJobApi, squareSyncStatusApi, listUsersApi, listTrucksApi, createTruckApi,
  listReviewRequestsApi, jobPortalUploadsApi, portalUploadUrl, apiErrorMessage,
} from "@/lib/api";

const POSITIONS = ["Driver", "Lead", "Helper", "Packer"];

const fmtPaidAt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";

const fmtJobDate = (d) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "Date TBD";

const PayRow = ({ label, info }) => {
  const status = info?.status || "unpaid";
  const Icon = status === "paid" ? CheckCircle2 : status === "pending" ? Clock3 : XCircle;
  const color = status === "paid" ? "text-success" : status === "pending" ? "text-warning" : "text-destructive";
  return (
    <div data-testid={`pay-row-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-2 text-sm">
      <Icon className={`w-4 h-4 shrink-0 ${color}`} />
      <span className="font-semibold text-primary">{label}:</span>
      {status === "paid" ? (
        <span className="text-ink-2"><Private><Money v={info.amount} /></Private> — paid {fmtPaidAt(info.paid_at)}</span>
      ) : status === "pending" ? (
        <span className="text-warning">Payment pending confirmation{info?.amount ? <> (<Private><Money v={info.amount} /></Private>)</> : ""}</span>
      ) : (
        <span className="text-faint">Not paid</span>
      )}
    </div>
  );
};

const Money = ({ v }) => <>{fmtMoney(v)}</>;

const PORTAL_LABELS = {
  gate_code: "Gate code", elevator: "Elevator", parking: "Parking",
  special_requests: "Requests", inventory_notes: "Inventory",
};

const PortalInfo = ({ job }) => {
  const [uploads, setUploads] = useState(null);
  const d = job.portal_details || {};
  const rows = Object.entries(PORTAL_LABELS).filter(([k]) => d[k]);
  if (!rows.length && !job.portal_review) return null;
  const token = job.tracking?.token;
  return (
    <div data-testid={`portal-info-${job.invoice_number}`} className="bg-accent/10 border border-accent/20 rounded-md p-3 space-y-1 text-sm">
      <p className="text-[11px] font-bold uppercase tracking-wide text-accent-ink">From the customer portal</p>
      {rows.map(([k, label]) => (
        <p key={k} className="text-ink-2"><strong>{label}:</strong> {d[k]}</p>
      ))}
      {job.portal_review && (
        <p data-testid={`portal-review-${job.invoice_number}`} className="text-ink-2">
          <strong>Review:</strong> {"★".repeat(job.portal_review.rating)}{"☆".repeat(5 - job.portal_review.rating)}
          {job.portal_review.text ? ` — "${job.portal_review.text}"` : ""}
        </p>
      )}
      {uploads === null ? (
        <button data-testid={`portal-uploads-btn-${job.invoice_number}`} className="text-xs font-semibold text-accent-ink hover:underline"
          onClick={() => jobPortalUploadsApi(job.id).then((r) => setUploads(r.uploads)).catch(() => setUploads([]))}>
          Show customer files
        </button>
      ) : uploads.length === 0 ? (
        <p className="text-xs text-faint">No files uploaded yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2 pt-1">
          {uploads.map((u) => (
            <a key={u.id} data-testid="portal-owner-upload" href={portalUploadUrl(token, u.id)} target="_blank" rel="noreferrer" className="block">
              {(u.content_type || "").startsWith("image/") ? (
                <img src={portalUploadUrl(token, u.id)} alt={u.filename} className="w-14 h-14 object-cover rounded border border-accent/25" />
              ) : (
                <span className="text-xs text-info underline">{u.filename}</span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const AddrLink = ({ label, addr, testId }) =>
  addr ? (
    <a data-testid={testId} href={mapsUrl(addr)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline decoration-border-strong hover:decoration-accent">
      <MapPin className="w-3.5 h-3.5 text-accent-ink shrink-0" />{label ? `${label}: ` : ""}{addr}
    </a>
  ) : (
    <span className="text-faint">{label ? `${label}: ` : ""}not set</span>
  );

function AssignDialog({ job, users, trucks, onSaved, onClose, onTruckCreated }) {
  const crewUsers = users.filter((u) => u.active !== false && (u.roles || [u.role]).includes("crew"));
  const [sel, setSel] = useState(() => (job.crew || []).map((c) => ({ user_id: c.user_id, position: c.position })));
  const [truckId, setTruckId] = useState(job.truck_id || "");
  const [newTruck, setNewTruck] = useState("");
  const [showNewTruck, setShowNewTruck] = useState(false);
  const [fields, setFields] = useState({
    pickup_address: job.pickup_address || "",
    dropoff_address: job.dropoff_address || "",
    job_date: job.job_date || "",
    start_time: job.start_time || "",
    truck_pickup_location: job.truck_pickup_location || "",
  });
  const [saving, setSaving] = useState(false);

  const toggle = (uid) =>
    setSel((s) => (s.some((x) => x.user_id === uid) ? s.filter((x) => x.user_id !== uid) : [...s, { user_id: uid, position: "Helper" }]));
  const setPos = (uid, position) => setSel((s) => s.map((x) => (x.user_id === uid ? { ...x, position } : x)));

  const addTruck = async () => {
    if (!newTruck.trim()) return;
    try {
      const t = await createTruckApi({ name: newTruck.trim() });
      onTruckCreated(t);
      setTruckId(t.id || t._id);
      setShowNewTruck(false);
      setNewTruck("");
      toast.success("Truck added.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await patchJobApi(job.id, { crew: sel, truck_id: truckId || "", ...fields });
      toast.success("Saved. Assigned crew can see this job right now.");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="assign-job-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Job #{job.invoice_number}</DialogTitle>
          <DialogDescription>Assign crew, truck, and addresses. Saving publishes it to each crew member's app.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Crew &amp; positions</Label>
            <div className="space-y-1.5 border border-border rounded-md p-2">
              {crewUsers.map((u) => {
                const picked = sel.find((x) => x.user_id === u.id);
                return (
                  <div key={u.id} data-testid={`crew-pick-${u.id}`} className="flex items-center gap-2">
                    <Checkbox data-testid={`crew-check-${u.id}`} checked={!!picked} onCheckedChange={() => toggle(u.id)} />
                    <span className="text-sm flex-1 text-primary">{u.name}</span>
                    {picked && (
                      <>
                        <Input data-testid={`crew-position-${u.id}`} className="w-24 h-8 text-xs" list="hy-positions"
                          value={picked.position} onChange={(e) => setPos(u.id, e.target.value)} />
                      </>
                    )}
                  </div>
                );
              })}
              {crewUsers.length === 0 && <p className="text-xs text-faint">No crew accounts yet — add them on the Crew page.</p>}
              <datalist id="hy-positions">
                {POSITIONS.map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
          </div>
          <div>
            <Label>Truck</Label>
            <div className="flex gap-2">
              <Select value={truckId || "none"} onValueChange={(v) => (v === "__new" ? setShowNewTruck(true) : setTruckId(v === "none" ? "" : v))}>
                <SelectTrigger data-testid="assign-truck-select" className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No truck yet</SelectItem>
                  {trucks.map((t) => <SelectItem key={t.id || t._id} value={t.id || t._id}>{t.name}</SelectItem>)}
                  <SelectItem value="__new">+ Add new truck…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showNewTruck && (
              <div className="flex gap-2 mt-2">
                <Input data-testid="new-truck-name-input" placeholder="Truck name (e.g. Box #3)" value={newTruck} onChange={(e) => setNewTruck(e.target.value)} />
                <Button data-testid="new-truck-save-btn" size="sm" onClick={addTruck}><Plus className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Job date</Label>
              <Input data-testid="assign-date-input" type="date" value={fields.job_date} onChange={(e) => setFields((s) => ({ ...s, job_date: e.target.value }))} />
            </div>
            <div>
              <Label>Start time</Label>
              <Input data-testid="assign-time-input" type="time" value={fields.start_time} onChange={(e) => setFields((s) => ({ ...s, start_time: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Pickup (from)</Label>
            <Input data-testid="assign-pickup-input" value={fields.pickup_address} onChange={(e) => setFields((s) => ({ ...s, pickup_address: e.target.value }))} />
          </div>
          <div>
            <Label>Drop-off (to)</Label>
            <Input data-testid="assign-dropoff-input" value={fields.dropoff_address} onChange={(e) => setFields((s) => ({ ...s, dropoff_address: e.target.value }))} />
          </div>
          <div>
            <Label>Truck pickup location</Label>
            <Input data-testid="assign-truck-pickup-input" value={fields.truck_pickup_location} onChange={(e) => setFields((s) => ({ ...s, truck_pickup_location: e.target.value }))} />
          </div>
          <Button data-testid="assign-save-btn" className="w-full gap-2 bg-accent hover:bg-accent-press" disabled={saving} onClick={save}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save &amp; publish to crew
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function JobsBoard() {
  const [jobs, setJobs] = useState(null);
  const [sync, setSync] = useState(null);
  const [users, setUsers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [editJob, setEditJob] = useState(null);
  const [portalJob, setPortalJob] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    listJobsApi().then(setJobs).catch(() => setJobs([]));
    squareSyncStatusApi().then(setSync).catch(() => {});
    listUsersApi().then(setUsers).catch(() => {});
    listTrucksApi().then(setTrucks).catch(() => {});
    listReviewRequestsApi().then(setReviews).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => listJobsApi().then(setJobs).catch(() => {}), 60000);
    return () => clearInterval(id);
  }, [load]);

  const upcoming = useMemo(
    () =>
      (jobs || []).filter((j) =>
        searchMatch(query, j.invoice_number, j.customer?.name, j.customer?.phone, j.truck_name, j.pickup_address, j.dropoff_address, ...(j.crew || []).map((c) => c.name))
      ),
    [jobs, query]
  );
  const reviewsShown = reviews.filter((r) => searchMatch(query, r.crew_name, r.customer?.name, r.customer?.phone, r.customer?.email, r.invoice_number));

  return (
    <div data-testid="jobs-board-page" className="space-y-5">
      <PageTitle title="Jobs" subtitle="Deposit-paid moves, labeled by Square invoice number." />
      {sync && !sync.webhook_connected && (
        <div data-testid="square-sync-banner" className="flex items-center gap-2 bg-warning/10 border border-warning/25 text-warning text-sm rounded-lg px-4 py-2.5">
          <PlugZap className="w-4 h-4 shrink-0" />
          <span>
            Square sync not connected — go to{" "}
            <Link to="/settings" className="font-bold underline">Settings → Integrations</Link>.
            {sync.square_connected ? " (A backup check still runs every 2 minutes.)" : ""}
          </span>
        </div>
      )}
      <InstructionBanner testId="jobs-board-banner">
        A job shows up here the moment its Square deposit is confirmed paid — never before. Tap Assign to pick the crew, truck, and addresses.
      </InstructionBanner>

      <SearchBar value={query} onChange={setQuery} placeholder="Search job #, customer, crew, truck, or address…" testId="jobs-search-input" />

      {jobs === null ? (
        <p className="text-sm text-faint">Loading…</p>
      ) : upcoming.length === 0 ? (
        <div data-testid="jobs-empty" className="surface p-8 text-center">
          <Briefcase className="w-10 h-10 text-faint/70 mx-auto" />
          <p className="text-sm text-faint mt-2">{query ? "No jobs match that search." : "No deposit-paid jobs yet. When a customer pays a Square deposit, the job pops up here on its own."}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {upcoming.map((job) => (
            <div key={job.id} data-testid={`job-card-${job.invoice_number}`} className="surface p-4 space-y-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-lg font-bold text-primary">
                    Job #{job.invoice_number}
                    <span className="text-faint font-medium"> — {fmtJobDate(job.job_date)}{job.start_time ? ` ${fmtTime12(job.start_time)}` : ""}</span>
                  </p>
                  <p className="text-sm text-faint">
                    {job.customer?.name || "Customer"}{job.customer?.phone ? ` · ${job.customer.phone}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid={`customer-page-btn-${job.invoice_number}`} size="sm" variant="outline" className="gap-1.5" onClick={() => setPortalJob(job)}>
                    <Send className="w-3.5 h-3.5" /> Customer page
                  </Button>
                  <Button data-testid={`assign-btn-${job.invoice_number}`} size="sm" variant="outline" className="gap-1.5" onClick={() => setEditJob(job)}>
                    <Pencil className="w-3.5 h-3.5" /> {job.crew?.length ? "Edit" : "Assign"}
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <PortalChip job={job} />
              </div>
              <div className="space-y-1 bg-surface-sunk rounded-md p-3">
                <PayRow label="Deposit Paid (25%)" info={job.deposit_paid} />
                <PayRow label="Paid in Full" info={job.paid_in_full} />
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
                <span className="inline-flex items-center gap-1.5 text-ink-2">
                  <Users className="w-3.5 h-3.5 text-faint" />
                  {job.crew?.length ? job.crew.map((c) => `${c.name.split(" ")[0]} (${c.position})`).join(", ") : <span className="text-faint">No crew yet</span>}
                </span>
                <span className="inline-flex items-center gap-1.5 text-ink-2">
                  <Truck className="w-3.5 h-3.5 text-faint" />
                  {job.truck_name || <span className="text-faint">No truck yet</span>}
                </span>
              </div>
              <div className="flex flex-col gap-1 text-sm">
                <AddrLink label="From" addr={job.pickup_address} testId={`maps-from-${job.invoice_number}`} />
                <AddrLink label="To" addr={job.dropoff_address} testId={`maps-to-${job.invoice_number}`} />
                <AddrLink label="Truck pickup" addr={job.truck_pickup_location} testId={`maps-truck-${job.invoice_number}`} />
              </div>
              <PortalInfo job={job} />
            </div>
          ))}
        </div>
      )}

      <div>
        <h2 className="text-sm font-bold uppercase tracking-wide text-faint mb-2 mt-8">Review requests sent by crew</h2>
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-faint border-b border-border">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Crew member</th>
                <th className="px-3 py-2">Channel</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Job</th>
              </tr>
            </thead>
            <tbody>
              {reviewsShown.map((r) => (
                <tr key={r.id} data-testid="review-log-row" className="border-b border-border">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtPaidAt(r.sent_at)}</td>
                  <td className="px-3 py-2">{r.crew_name}</td>
                  <td className="px-3 py-2 uppercase text-xs font-bold text-faint">{r.channel}</td>
                  <td className="px-3 py-2">{r.customer?.name}</td>
                  <td className="px-3 py-2">{r.customer?.phone}</td>
                  <td className="px-3 py-2">{r.customer?.email}</td>
                  <td className="px-3 py-2">#{r.invoice_number}</td>
                </tr>
              ))}
              {reviewsShown.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-5 text-center text-faint">{query ? "No review requests match that search." : "No review requests yet."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editJob && (
        <AssignDialog
          job={editJob}
          users={users}
          trucks={trucks}
          onSaved={() => { setEditJob(null); load(); }}
          onClose={() => setEditJob(null)}
          onTruckCreated={(t) => setTrucks((list) => [...list, t])}
        />
      )}
      {portalJob && (
        <CustomerPageDialog
          jobId={portalJob.id}
          invoiceNumber={portalJob.invoice_number}
          onClose={() => setPortalJob(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
